import Analytics from '@/utils/analytics';
import {
  AvailabilityMap,
  CatalogCategoryType,
  FilteredCategoryItemGroup,
  ItemListOption,
  VariantPackGroup,
} from '@/contexts/item-context-types';
import { gql } from '@apollo/client';
import {
  SearchSortOrder as GraphQLSearchSortOrder,
  ItemsContext_VariantsPackFieldsFragment,
  PaidSearchEventData,
  SearchCatalog_VariantPackFieldsFragment,
  SearchFilterResponse,
  SearchPublicSupplierCatalog_ItemsQuery,
  SearchPublicSupplierCatalog_ItemsQueryVariables,
  SearchSupplierCatalog_ItemsQuery,
  SearchSupplierCatalog_ItemsQueryVariables,
} from '@/types';
import {
  isVariantPackInCategory,
  isVariantPackInItemGroupCategory,
} from '@/utils/category';
import { SearchEntryPoint } from '@/screens/partners/types';
import {
  SearchFiltersMap,
  VariantPackOrderCountsMap,
} from '@/screens/order-inbounds/types';
import { SearchSortOrder, useUserContext } from '@/contexts/user-context';
import { TypesenseSearchFilters } from '@/utils/search-filters';
import { useAdaptAndGroupVariantPacks } from './use-adapt-and-group-item-list-options';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDynamicConfigValue } from './use-dynamic-config-value';
import { useGetAddToOrderDisabled } from './use-add-to-order-disabled';
import { useGTINMetadataFilters } from './use-gtin-metadata-filters';
import { useItemQueryVariables } from './use-item-query-variables';
import { useLazyQuery } from './use-lazy-query';
import { useMaybeAuthStackContext } from '@/contexts/auth-stack-context';
import { useNewOrderActionsContext } from '@/contexts/new-order-actions-context';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useOfflineModeActive } from './use-offline-mode-active';
import { useSearchFilters } from './catalog-search/use-search-filters';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';
import { validate as uuidValidate } from 'uuid';
import {
  VARIANTS_PACK_FRAGMENT,
  VARIANTS_PACK_ITEM_GROUPS_MEMBERSHIP_FRAGMENT,
} from '@/contexts/items-context-queries';

export const SEARCH_VARIANTS_PACK_FRAGMENT = gql`
  fragment SearchCatalog_VariantPackFields on variants_packs {
    ...ItemsContext_VariantsPackFields
    ...ItemsContext_VariantsPackItemGroupsMembershipFields
  }

  ${VARIANTS_PACK_FRAGMENT}
  ${VARIANTS_PACK_ITEM_GROUPS_MEMBERSHIP_FRAGMENT}
`;

export const SEARCH_FILTERS_FRAGMENT = gql`
  fragment SearchCatalog_SearchFilterFields on SearchFilterResponse {
    display_strategy
    name {
      original_value
      translated_value
    }
    type
    values {
      original_value
      translated_value
    }
  }
`;

export const SEARCH_QUERY = gql`
  query SearchSupplierCatalog_Items(
    $locale: String!
    $query: String!
    $restaurantUUID: uuid!
    $supplierUUID: uuid!
    $variantPackGroupUUID: uuid
    $searchSortOrder: SearchSortOrder
    $availabilityFulfillmentDate: String
    $fulfillmentDate: String
    $filters: [SearchFilterInput!]
    $includeOrderCounts: Boolean
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
    $offset: Int
  ) {
    searchVariantPacks(
      include_order_counts: $includeOrderCounts
      query: $query
      restaurant_id: $restaurantUUID
      filters: $filters
      supplier_id: $supplierUUID
      search_sort_order: $searchSortOrder
      availability_fulfillment_date: $availabilityFulfillmentDate
      fulfillment_date: $fulfillmentDate
      variant_pack_group_id: $variantPackGroupUUID
      offset: $offset
    ) {
      available_quantity
      total_count
      next_offset
      filters {
        ...SearchCatalog_SearchFilterFields
      }
      paid_search_event_data {
        ad_campaign_id
        ad_manager_id
        add_to_cart_event_id
        advertiser_id
        click_event_id
        impression_event_id
        gtin
        matching_keyword
        purchase_event_id
        variant_pack_id
      }
      variant_pack {
        ...SearchCatalog_VariantPackFields
      }
      variant_pack_order_counts {
        days
        order_count
      }
    }
  }

  ${SEARCH_VARIANTS_PACK_FRAGMENT}
  ${SEARCH_FILTERS_FRAGMENT}
`;

export const PUBLIC_SEARCH_QUERY = gql`
  query SearchPublicSupplierCatalog_Items(
    $locale: String!
    $query: String!
    $restaurantUUID: uuid!
    $filters: [SearchFilterInput!]
    $supplierUUID: uuid!
    $skipGroupMemberships: Boolean!
    $skipLiveSalt: Boolean!
    $skipSaltApi: Boolean!
    $useSaltCache: Boolean!
    $variantPackGroupUUID: uuid
    $offset: Int
  ) {
    searchPublicVariantPacks(
      filters: $filters
      query: $query
      supplier_id: $supplierUUID
      variant_pack_group_id: $variantPackGroupUUID
      offset: $offset
    ) {
      filters {
        ...SearchCatalog_SearchFilterFields
      }
      variant_pack {
        ...SearchCatalog_VariantPackFields
      }
    }
  }

  ${SEARCH_VARIANTS_PACK_FRAGMENT}
  ${SEARCH_FILTERS_FRAGMENT}
`;

/** Map the UserSetting for SearchSortOrder to the GraphQL variable. */
export const searchSortOrderMap = {
  [SearchSortOrder.Relevance]: GraphQLSearchSortOrder.Relevance,
  [SearchSortOrder.Alphabetical]: GraphQLSearchSortOrder.DisplayName,
  [SearchSortOrder.AlphabeticalReverse]:
    GraphQLSearchSortOrder.DisplayNameReverse,
  [SearchSortOrder.ItemCode]: GraphQLSearchSortOrder.ItemCode,
  [SearchSortOrder.ItemCodeReverse]: GraphQLSearchSortOrder.ItemCodeReverse,
  [SearchSortOrder.PurchasedDate]: GraphQLSearchSortOrder.PurchasedDate,
};

export type PaidSearchMap = Partial<Record<string, PaidSearchEventData>>;

interface VariantPackSearchFiltersMap {
  [variantPackUUID: string]: SearchFilterResponse[];
}

export interface SearchParams {
  availabilityFulfillmentDate?: string;
  category?: CatalogCategoryType;
  includeGTINFilters?: boolean;
  includeOrderCounts?: boolean;
  initialSearchText?: string;
  searchEntryPoint?: SearchEntryPoint;
  searchSource: string;
}

export interface SearchVariantPacksReturn {
  availabilityMap: AvailabilityMap;
  badgesFilter: Set<string>;
  categories: string[];
  categoryFilter: string | null;
  categoryItemGroupFilter: FilteredCategoryItemGroup | null;
  categoryItemGroups: VariantPackGroup[];
  clearSearch: () => void;
  fetchMoreOffset: number | null;
  fetchNextPage: (searchSortOrder: SearchSortOrder) => Promise<void>;
  gtinFilters: ReturnType<typeof useGTINMetadataFilters>['gtinFilters'];
  items: ItemListOption[] | null;
  matchingVariantPackUUIDs: ReturnType<
    typeof useGTINMetadataFilters
  >['matchingVariantPackUUIDs'];
  queryLoading: boolean;
  queryLoadingMore: boolean;
  queryRef: React.RefObject<string>;
  searchFilters: TypesenseSearchFilters;
  searchItemsCallback: (
    query: string,
    searchSortOrder: SearchSortOrder,
    selectedCategory?: CatalogCategoryType,
    offset?: number,
  ) => Promise<void>;
  searchResults: SearchCatalog_VariantPackFieldsFragment[] | null;
  setBadgesFilter: React.Dispatch<React.SetStateAction<Set<string>>>;
  setCategoryFilter: React.Dispatch<React.SetStateAction<string | null>>;
  setCategoryItemGroupFilter: React.Dispatch<
    React.SetStateAction<FilteredCategoryItemGroup | null>
  >;
  setSubcategoriesFilter: React.Dispatch<React.SetStateAction<Set<string>>>;
  subcategoriesFilter: Set<string>;
}

export const useSearchVariantPacks = ({
  availabilityFulfillmentDate,
  category,
  includeGTINFilters,
  includeOrderCounts,
  initialSearchText,
  searchEntryPoint,
  searchSource,
}: SearchParams): SearchVariantPacksReturn => {
  const adaptAndGroupVariantPacks = useAdaptAndGroupVariantPacks();
  const itemQueryVariables = useItemQueryVariables();
  const { restaurantUUID, supplierUUID } = useNewOrderContext();
  const { hasInfoListResponse, variantPackGroupMap, childVariantPackGroupMap } =
    useVariantPackInfoContext();
  const { selectedFulfillment } = useNewOrderActionsContext();
  const { isGuestMode } = useUserContext();
  const maybeAuthStack = useMaybeAuthStackContext();
  const isOfflineModeActive = useOfflineModeActive();

  const queryRef = useRef(initialSearchText || '');
  const [searchResults, setSearchResults] = useState<
    ItemsContext_VariantsPackFieldsFragment[] | null
  >(null);
  const [filters, setFilters] = useState<SearchFilterResponse[] | null>(null);
  const [variantPackSearchFiltersMap, setVariantPackSearchFiltersMap] =
    useState<VariantPackSearchFiltersMap>({});
  const [availabilityMap, setAvailabilityMap] = useState<AvailabilityMap>({});
  const [paidSearchMap, setPaidSearchMap] = useState<PaidSearchMap>({});
  const [variantPackOrderCountsMap, setVariantPackOrderCountsMap] =
    useState<VariantPackOrderCountsMap>(new Map());
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [categoryItemGroupFilter, setCategoryItemGroupFilter] =
    useState<FilteredCategoryItemGroup | null>(null);
  const [badgesFilter, setBadgesFilter] = useState<Set<string>>(new Set());
  const [subcategoriesFilter, setSubcategoriesFilter] = useState<Set<string>>(
    new Set(),
  );
  const [queryLoading, setQueryLoading] = useState(false);
  const [queryLoadingMore, setQueryLoadingMore] = useState(false);
  const queryLoadingMoreRef = useRef(false);
  const [fetchMoreOffset, setFetchMoreOffset] = useState<number | null>(null);

  // incrementing counter to discard results from stale requests
  // a "stale" request can happen when user fetches next page, then enters search term without waiting for result
  const latestRequestRef = useRef(0);

  const { matchingVariantPackUUIDs, gtinFilters } = useGTINMetadataFilters(
    searchResults || [],
    Boolean(includeGTINFilters),
  );
  const searchFilters = useSearchFilters({
    category,
    filters,
    searchEntryPoint,
  });

  const itemGroupsEnabled = useDynamicConfigValue('item_groups_enabled');
  const catalogSearchUXEnabled = useDynamicConfigValue(
    'catalog_search_ux_enabled',
  );
  const getIsItemDisabled = useGetAddToOrderDisabled();

  const searchItems = useLazyQuery<
    SearchSupplierCatalog_ItemsQuery,
    SearchSupplierCatalog_ItemsQueryVariables
  >(SEARCH_QUERY, {
    fetchPolicy: 'no-cache',
  });

  const searchPublicItems = useLazyQuery<
    SearchPublicSupplierCatalog_ItemsQuery,
    SearchPublicSupplierCatalog_ItemsQueryVariables
  >(PUBLIC_SEARCH_QUERY, {
    fetchPolicy: 'no-cache',
  });

  const convertFilterResponseToInput = (
    filterResponse: SearchFilterResponse,
  ) => {
    return {
      field: filterResponse.name.original_value,
      values: filterResponse.values?.map((value) => value.original_value) ?? [],
    };
  };

  const searchItemsCallback = useCallback(
    async (
      query: string,
      searchSortOrder: SearchSortOrder,
      selectedCategory?: CatalogCategoryType,
      offset?: number,
    ) => {
      // if this call is a result of paging/scrolling or first load
      const isAppending = typeof offset === 'number';

      if (!isAppending) {
        queryRef.current = query;
      }

      // a fresh search becomes "the latest"; an append belongs to whatever search is current
      const requestId = isAppending
        ? latestRequestRef.current
        : ++latestRequestRef.current;

      const isFiltersSelected = Boolean(searchFilters.selected?.length);

      const variantPackGroupUUID =
        selectedCategory?.uuid && uuidValidate(selectedCategory?.uuid)
          ? selectedCategory?.uuid
          : undefined;

      if (
        (!queryRef.current && !isFiltersSelected && !selectedCategory) ||
        isOfflineModeActive
      ) {
        setSearchResults(null);
        setFilters(null);
        setVariantPackSearchFiltersMap({});
        return;
      }

      if (!isAppending) {
        const eventPayload = {
          category,
          query,
          restaurantUUID,
          searchSortOrder,
          supplierUUID,
          variantPackGroupUUID,
        };

        // Track all searches as the same event, with an additional source property
        Analytics.track('search_variant_packs', {
          source: searchSource,
          ...eventPayload,
        });
        // Maintain the legacy event name for backwards compatibility
        Analytics.track(`${searchSource}.search`, eventPayload);
      }

      if (!isAppending) {
        setQueryLoading(true);
      } else {
        queryLoadingMoreRef.current = true;
        setQueryLoadingMore(true);
      }

      try {
        const searchResult: SearchSupplierCatalog_ItemsQuery['searchVariantPacks'] =
          isGuestMode && maybeAuthStack?.publicRestaurant
            ? ((
                await searchPublicItems({
                  context: {
                    anonymous: true,
                  },
                  variables: {
                    ...itemQueryVariables,
                    filters: isFiltersSelected
                      ? searchFilters.selected.map((f) =>
                          convertFilterResponseToInput(f),
                        )
                      : undefined,
                    offset,
                    query,
                    restaurantUUID:
                      maybeAuthStack.publicRestaurant.restaurantUUID,
                    supplierUUID: maybeAuthStack.publicRestaurant.supplierUUID,
                    variantPackGroupUUID,
                  },
                })
              ).data
                .searchPublicVariantPacks as SearchSupplierCatalog_ItemsQuery['searchVariantPacks'])
            : (
                await searchItems({
                  variables: {
                    ...itemQueryVariables,
                    availabilityFulfillmentDate: availabilityFulfillmentDate,
                    filters: isFiltersSelected
                      ? searchFilters.selected.map((f) =>
                          convertFilterResponseToInput(f),
                        )
                      : undefined,
                    fulfillmentDate: selectedFulfillment?.date,
                    includeOrderCounts,
                    offset,
                    query,
                    restaurantUUID: restaurantUUID!,
                    searchSortOrder: searchSortOrderMap[searchSortOrder],
                    supplierUUID: supplierUUID!,
                    variantPackGroupUUID,
                  },
                })
              ).data.searchVariantPacks;

        // Discard stale responses: a fresh search bumps latestRequestRef,
        // so any in-flight request with an older token is ignored.
        // Appends inherit the current token without bumping it.
        if (requestId !== latestRequestRef.current) {
          return;
        }

        setQueryLoading(false);
        queryLoadingMoreRef.current = false;
        setQueryLoadingMore(false);

        {
          const nextAvailabilityMap: AvailabilityMap = {};
          const nextPaidSearchMap: PaidSearchMap = {};
          const nextVariantPackOrderCountsMap: VariantPackOrderCountsMap =
            new Map();
          const nextSearchFiltersMap: VariantPackSearchFiltersMap = {};

          const matchingVariantPacks = searchResult.reduce<
            ItemsContext_VariantsPackFieldsFragment[]
          >((result, vp) => {
            if (vp.variant_pack) {
              const variantPackUUID = vp.variant_pack.uuid;

              const isInsideCategory =
                itemGroupsEnabled && typeof category !== 'undefined'
                  ? isVariantPackInItemGroupCategory(
                      category,
                      vp.variant_pack,
                      childVariantPackGroupMap[category.uuid!],
                    )
                  : isVariantPackInCategory(vp.variant_pack, category);
              const shouldIncludeVariantPack =
                catalogSearchUXEnabled || isInsideCategory;

              if (typeof category === 'undefined' || shouldIncludeVariantPack) {
                const inStockQuantity = vp.available_quantity;
                if (typeof inStockQuantity === 'number') {
                  nextAvailabilityMap[variantPackUUID] = inStockQuantity;
                }
                result.push(vp.variant_pack);
              }

              if (catalogSearchUXEnabled && vp.filters) {
                nextSearchFiltersMap[variantPackUUID] = vp.filters;
              }

              if (
                vp.paid_search_event_data &&
                hasInfoListResponse &&
                !getIsItemDisabled(variantPackUUID)
              ) {
                nextPaidSearchMap[variantPackUUID] = vp.paid_search_event_data;
              }

              if (vp.variant_pack_order_counts) {
                nextVariantPackOrderCountsMap.set(
                  variantPackUUID,
                  vp.variant_pack_order_counts,
                );
              }
            }

            return result;
          }, []);

          const lastResult =
            searchResult.length > 0
              ? searchResult[searchResult.length - 1]
              : {};

          const nextOffset = lastResult.next_offset || 0;
          const totalCount = lastResult.total_count || 0;
          const hasMorePages = nextOffset < totalCount && nextOffset !== 0;

          // fetching next page, need to keep what was already fetched before
          if (isAppending) {
            setSearchResults((prev) => [
              ...(prev || []),
              ...matchingVariantPacks,
            ]);
            setAvailabilityMap((prev) => ({
              ...prev,
              ...nextAvailabilityMap,
            }));
            setPaidSearchMap((prev) => ({ ...prev, ...nextPaidSearchMap }));
            setVariantPackOrderCountsMap((prev) => {
              const merged = new Map(prev);
              nextVariantPackOrderCountsMap.forEach((v, k) => merged.set(k, v));
              return merged;
            });
            setVariantPackSearchFiltersMap((prev) => ({
              ...prev,
              ...nextSearchFiltersMap,
            }));
          } else {
            setSearchResults(matchingVariantPacks);
            setAvailabilityMap(nextAvailabilityMap);
            setPaidSearchMap(nextPaidSearchMap);
            setVariantPackOrderCountsMap(nextVariantPackOrderCountsMap);
            setVariantPackSearchFiltersMap(nextSearchFiltersMap);
          }

          setFetchMoreOffset(hasMorePages ? nextOffset : null);
        }
      } catch (e) {
        // If it's the latest query that failed, reset state and allow caller to
        // handle the error. This will effectively silence errors from queries
        // that are no longer relevant.
        if (requestId === latestRequestRef.current) {
          setQueryLoading(false);
          queryLoadingMoreRef.current = false;
          setQueryLoadingMore(false);
          throw e;
        }
      }
    },
    [
      availabilityFulfillmentDate,
      catalogSearchUXEnabled,
      category,
      childVariantPackGroupMap,
      getIsItemDisabled,
      hasInfoListResponse,
      includeOrderCounts,
      isGuestMode,
      isOfflineModeActive,
      itemGroupsEnabled,
      itemQueryVariables,
      maybeAuthStack,
      restaurantUUID,
      searchFilters.selected,
      searchItems,
      searchPublicItems,
      searchSource,
      selectedFulfillment?.date,
      supplierUUID,
    ],
  );

  const fetchNextPage = useCallback(
    async (searchSortOrder: SearchSortOrder) => {
      if (fetchMoreOffset === null || queryLoadingMoreRef.current) {
        return;
      }

      await searchItemsCallback(
        queryRef.current,
        searchSortOrder,
        category,
        fetchMoreOffset,
      );
    },
    [fetchMoreOffset, searchItemsCallback, category],
  );

  const executedInitialSearch = useRef(false);
  useEffect(() => {
    if (initialSearchText && !executedInitialSearch.current) {
      executedInitialSearch.current = true;
      searchItemsCallback(initialSearchText, SearchSortOrder.Relevance);
    }
  }, [initialSearchText, searchFilters.selected, searchItemsCallback]);

  const updatePaidSearchVPMetadata = useCallback(
    (items: ItemListOption[]) => {
      return items.reduce<ItemListOption[]>((acc, item) => {
        let matchedVPPaidSearch: PaidSearchEventData | null = null;
        const withPaidSearchSkus = item.metadata!.skus.map((sku) => {
          const paidSearch = paidSearchMap[sku.variantPackUUID];
          if (paidSearch) {
            matchedVPPaidSearch = paidSearch;
          }

          return {
            ...sku,
            catalogSearchResult: true,
            paidSearch,
          };
        });

        if (matchedVPPaidSearch) {
          // move promoted items to the front of the list
          acc.unshift({
            ...item,
            metadata: {
              ...item.metadata!,
              catalogSearchResult: true,
              paidSearch: matchedVPPaidSearch,
              skus: withPaidSearchSkus,
            },
          });
        }
        // add organic result in place
        acc.push({
          ...item,
          metadata: {
            ...item.metadata!,
            catalogSearchResult: true,
            skus: item.metadata!.skus.map((sku) => {
              return {
                ...sku,
                catalogSearchResult: true,
              };
            }),
          },
        });
        return acc;
      }, []);
    },
    [paidSearchMap],
  );

  const items = useMemo<ItemListOption[] | null>(() => {
    if (!searchResults) {
      return null;
    }

    const getFilteredByCategory = (
      searchResults: ItemsContext_VariantsPackFieldsFragment[],
    ) => {
      if (!category?.uuid) {
        return searchResults;
      }

      return searchResults.filter((item) =>
        itemGroupsEnabled
          ? isVariantPackInItemGroupCategory(
              category,
              item,
              childVariantPackGroupMap[category.uuid!],
            )
          : isVariantPackInCategory(item, category),
      );
    };

    const results = catalogSearchUXEnabled
      ? getFilteredByCategory(searchResults)
      : searchResults;

    return updatePaidSearchVPMetadata(
      adaptAndGroupVariantPacks(
        results || [],
        undefined,
        variantPackOrderCountsMap,
      ),
    );
  }, [
    adaptAndGroupVariantPacks,
    catalogSearchUXEnabled,
    category,
    childVariantPackGroupMap,
    itemGroupsEnabled,
    searchResults,
    updatePaidSearchVPMetadata,
    variantPackOrderCountsMap,
  ]);

  useEffect(() => {
    if (!items) {
      return;
    }

    const nextFiltersMap: SearchFiltersMap = new Map();

    for (const item of items) {
      const filters =
        variantPackSearchFiltersMap[item.metadata!.variantPackUUID] || [];
      for (const filter of filters) {
        nextFiltersMap.set(filter.name.original_value, {
          ...nextFiltersMap.get(filter.name.original_value),
          ...filter,
        });
      }
    }

    setFilters(Array.from(nextFiltersMap.values()));
  }, [items, variantPackSearchFiltersMap]);

  const categories: string[] = useMemo(() => {
    const uniqueCategories = new Set(
      (items || [])
        .map((item) => item.metadata?.category || '')
        .filter(Boolean),
    );

    return uniqueCategories.size > 1 ? Array.from(uniqueCategories).sort() : [];
  }, [items]);

  const categoryItemGroups: VariantPackGroup[] = useMemo(() => {
    const uniqueCategoryItemGroupsObject = (items || []).reduce(
      (acc: { [key: string]: VariantPackGroup }, item) => {
        const variantPackGroupCategoryUUIDs =
          item.metadata?.variantPackGroupCategoryUUIDs || [];
        const categoryChildGroupUUIDs = category?.uuid
          ? childVariantPackGroupMap[category.uuid]
          : null;
        // Filter only item groups related to the catalog category for search in catalog category
        const uuids = categoryChildGroupUUIDs
          ? variantPackGroupCategoryUUIDs.filter((uuid) =>
              categoryChildGroupUUIDs.includes(uuid),
            )
          : variantPackGroupCategoryUUIDs;

        for (const uuid of uuids) {
          const categoryItemGroup = variantPackGroupMap[uuid];
          if (!acc[uuid] && categoryItemGroup) {
            acc[uuid] = categoryItemGroup;
          }
        }
        return acc;
      },
      {},
    );

    const uniqueCategoryItemGroups = Object.values(
      uniqueCategoryItemGroupsObject,
    );

    return uniqueCategoryItemGroups.length > 0
      ? uniqueCategoryItemGroups.sort((a, b) =>
          a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
        )
      : [];
  }, [items, category, childVariantPackGroupMap, variantPackGroupMap]);

  const filteredItems = useMemo(() => {
    const isFiltersAvailable = itemGroupsEnabled
      ? includeGTINFilters || categoryItemGroupFilter || badgesFilter
      : includeGTINFilters || categoryFilter;

    if (!items || !isFiltersAvailable) {
      return items;
    }
    return items.filter((i) => {
      let matchesBadges = true;
      let matchesCategory = true;
      let matchesGTINMetadata = true;
      let matchesSubcategory = true;

      // badges filter
      if (badgesFilter.size > 0) {
        matchesBadges = (i.metadata?.skus || []).some((sku) =>
          sku.badges?.some((b) =>
            Array.from(badgesFilter).some((uuid) => b.uuid === uuid),
          ),
        );
      }

      // subcategories filter
      if (subcategoriesFilter.size > 0) {
        const selectedSubcategoryUUIDs = new Set(
          Array.from(subcategoriesFilter).map((path) => {
            const segments = path.split('/');
            return segments[segments.length - 1];
          }),
        );
        matchesSubcategory = (i.metadata?.skus || []).some((sku) =>
          sku.variantPackGroupCategoryUUIDs?.some((uuid) =>
            selectedSubcategoryUUIDs.has(variantPackGroupMap[uuid]?.uuid!),
          ),
        );
      }

      if (categoryFilter) {
        matchesCategory = i.metadata?.category === categoryFilter;
      }
      if (categoryItemGroupFilter && categoryItemGroupFilter.uuid) {
        matchesCategory = (i.metadata?.skus || []).some((sku) =>
          sku.variantPackGroupCategoryUUIDs?.some(
            (uuid) =>
              variantPackGroupMap[uuid]?.uuid === categoryItemGroupFilter.uuid,
          ),
        );
      }
      if (includeGTINFilters) {
        matchesGTINMetadata = (i.metadata?.skus || []).some((sku) =>
          matchingVariantPackUUIDs.has(sku.variantPackUUID),
        );
      }
      return (
        matchesCategory &&
        matchesGTINMetadata &&
        matchesBadges &&
        matchesSubcategory
      );
    });
  }, [
    itemGroupsEnabled,
    includeGTINFilters,
    categoryItemGroupFilter,
    badgesFilter,
    categoryFilter,
    items,
    subcategoriesFilter,
    variantPackGroupMap,
    matchingVariantPackUUIDs,
  ]);

  return {
    availabilityMap,
    badgesFilter,
    categories,
    categoryFilter,
    categoryItemGroupFilter,
    categoryItemGroups,
    clearSearch: () => {
      queryRef.current = '';
      setSearchResults(null);
      setFilters(null);
      searchFilters.resetFilters();
      setFetchMoreOffset(null);
      ++latestRequestRef.current; // so stale request won't get processed if it comes back after a clear
    },
    fetchMoreOffset,
    fetchNextPage,
    gtinFilters,
    items: filteredItems,
    matchingVariantPackUUIDs,
    queryLoading,
    queryLoadingMore,
    queryRef,
    searchFilters,
    searchItemsCallback,
    searchResults,
    setBadgesFilter,
    setCategoryFilter,
    setCategoryItemGroupFilter,
    setSubcategoriesFilter,
    subcategoriesFilter,
  };
};
